import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { pricingPlans } from '../mock';

export const Pricing = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Simple, Transparent Pricing</h1>
          <p className="text-xl text-gray-600">
            Choose the plan that fits your business needs. No hidden fees, no surprises.
          </p>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative hover:shadow-2xl transition-all duration-300 ${
                  plan.popular 
                    ? 'border-blue-600 border-2 shadow-xl scale-105' 
                    : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center space-x-1 shadow-lg">
                      <Sparkles size={14} />
                      <span>Most Popular</span>
                    </span>
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-sm text-gray-600 mb-6">{plan.description}</p>
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-gray-900 mb-1">{plan.price}</div>
                    <div className="text-sm text-gray-600">{plan.period}</div>
                  </div>
                  <Link to="/contact" className="block mb-6">
                    <Button 
                      className={`w-full ${
                        plan.popular
                          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                      } transition-all duration-200`}
                    >
                      Get Started
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                  <div className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about our pricing</p>
          </div>
          <div className="space-y-6">
            {[
              {
                question: 'What happens after the setup fee?',
                answer: 'After the initial setup, Business and Enterprise plans include ongoing support. Starter plan clients can opt for pay-as-you-go support or upgrade to a support plan.'
              },
              {
                question: 'Can I switch plans later?',
                answer: 'Absolutely! You can upgrade or adjust your plan at any time. We\'ll work with you to ensure a smooth transition.'
              },
              {
                question: 'Are there any hidden fees?',
                answer: 'No hidden fees. The only additional costs would be third-party licenses (Microsoft 365, Azure, Google Workspace) which are billed directly by the providers.'
              },
              {
                question: 'Do you offer custom packages?',
                answer: 'Yes! Our Enterprise plan is fully customizable. Contact us to discuss your specific requirements and we\'ll create a tailored solution.'
              },
              {
                question: 'What payment methods do you accept?',
                answer: 'We accept bank transfers, credit cards, and can arrange invoice-based billing for Business and Enterprise clients.'
              }
            ].map((faq, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Still Have Questions?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Book a free consultation to discuss which plan is right for your business.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl hover:shadow-2xl transition-all duration-200">
              Schedule Free Consultation
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
